import React from 'react';
import { X, Check, Sparkles, Pause } from 'lucide-react';
import { useStreak } from '../../contexts/StreakContext';
import LottieFire from './LottieFire';

export default function StreakNotificationModal() {
  const { streakDetails, showStreakModal, closeStreakModal } = useStreak();

  if (!showStreakModal || !streakDetails) return null;

  const {
    streak = 0,
    is7DayMilestone = false,
    weeklyDays = []
  } = streakDetails;

  // Dynamically compute in-between pause states so any missed day between activity
  // always displays the Pause UI, even with cached or older data
  const processedDays = weeklyDays.map((d, i) => {
    if (d.completed) {
      return { ...d, isPaused: false, isInBetween: false };
    }

    const hasPriorCompleted = weeklyDays.slice(0, i).some(prev => prev.completed);
    const hasLaterCompleted = weeklyDays.slice(i + 1).some(next => next.completed);
    const isInBetween = hasPriorCompleted && hasLaterCompleted;
    const isPastMissed = d.isPast && hasPriorCompleted;
    // Days prior to the first completed activity of the week should be empty/nothing, NOT paused
    const isPaused = hasPriorCompleted ? Boolean(d.isPaused || isInBetween || isPastMissed) : false;

    return {
      ...d,
      isPaused,
      isInBetween
    };
  });

  return (
    <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-[100000] animate-slide-in-right pointer-events-auto">
      <div className="relative flex flex-col sm:flex-row items-stretch bg-[#363431] text-white rounded-lg shadow-2xl border-2 border-[#4a4743] min-w-[340px] sm:min-w-[520px] max-w-[600px] min-h-[110px] sm:min-h-[120px] h-auto overflow-visible">
        {/* Floating Top-Right Close Button */}
        <button
          onClick={closeStreakModal}
          className="absolute -top-3 -right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#211f1e] hover:bg-[#2c2a28] text-stone-400 hover:text-white flex items-center justify-center border-2 border-[#4a4743] shadow-lg cursor-pointer z-20 transition-all hover:scale-110 active:scale-95"
          aria-label="Close notification"
        >
          <X size={16} className="stroke-[2.5]" />
        </button>

        {/* Left Profile / Flame Avatar Box */}
        <div className="w-full sm:w-36 bg-gradient-to-b from-[#2d2a28] to-[#211f1e] flex flex-row sm:flex-col items-center justify-center p-2 shrink-0 rounded-t-lg sm:rounded-t-none sm:rounded-l-lg border-b sm:border-b-0 sm:border-r-2 border-[#4a4743] relative overflow-hidden">
          {/* Subtle Ambient Glow */}
          <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/25 via-orange-500/20 to-transparent pointer-events-none" />
          
          <div className="relative flex items-center justify-center my-auto">
            <LottieFire className="w-28 h-28 sm:w-32 sm:h-32 scale-125" />
            {is7DayMilestone && (
              <Sparkles size={22} className="absolute -top-1 -right-1 text-yellow-300 animate-spin pointer-events-none" />
            )}
          </div>

          <span className="ml-3 sm:ml-0 sm:-mt-1 text-xs sm:text-sm font-black tracking-wider text-amber-300 uppercase drop-shadow z-10 pb-0.5">
            {streak} {streak === 1 ? 'Day' : 'Days'}
          </span>
        </div>

        {/* Middle Content Section */}
        <div className="px-4 sm:px-6 py-3.5 flex-1 min-w-0 flex flex-col justify-center pr-8 sm:pr-9">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="font-black text-base sm:text-lg text-white leading-tight flex items-center gap-1.5">
              Keep Going!
            </span>
          </div>

          <p className="text-xs text-stone-300 font-medium mt-1">
            {is7DayMilestone
              ? 'Incredible dedication! You conquered the full 7-day streak cycle.'
              : 'Awesome job! Keep your daily chess streak alive.'}
          </p>

          {/* 7-Day Visual Tracker (S M T W T F S) */}
          <div className="flex items-center justify-between gap-1.5 sm:gap-2 mt-3 pt-2.5 border-t border-stone-700/60">
            {processedDays.map((d, i) => (
              <div key={d.date || i} className="flex flex-col items-center gap-1.5 flex-1">
                {/* Square Day Indicator Above Day Letter */}
                <div className="relative flex items-center justify-center h-8 sm:h-9">
                  {d.completed ? (
                    <div 
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-tr from-amber-500 to-orange-500 border border-amber-300 shadow-md shadow-orange-500/30 flex items-center justify-center text-white"
                      title={`${d.dayName}: Completed ✓`}
                    >
                      <Check size={16} className="stroke-[3.5] text-white drop-shadow-sm" />
                    </div>
                  ) : d.isPaused ? (
                    <div 
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-tr from-purple-600 via-indigo-600 to-blue-500 border-2 border-indigo-300/90 shadow-md shadow-indigo-500/40 ring-1 ring-purple-400/30 flex items-center justify-center text-white transition-transform hover:scale-110"
                      title={`${d.dayName}: Paused (No activity in between) ⏸`}
                    >
                      <Pause size={13} className="text-white fill-white" />
                    </div>
                  ) : d.isToday ? (
                    <div 
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-amber-500/10 border-2 border-dashed border-amber-400/80 flex items-center justify-center animate-pulse"
                      title="Today: Awaiting activity"
                    />
                  ) : (
                    <div 
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#272523] border border-stone-700/80 flex items-center justify-center"
                      title={d.dayName}
                    />
                  )}
                </div>

                {/* Day Letter Below (S M T W T F S) */}
                <span className="text-xs sm:text-sm font-bold text-stone-300 uppercase tracking-wider">
                  {d.dayLetter}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

